import { listMaintenanceTasksForItem } from '@/modules/maintenance/services/maintenanceRepository';
import {
  MAINTENANCE_STATUS_LABELS,
  MAINTENANCE_TYPE_LABELS,
} from '@/modules/maintenance/types/maintenance';
import { listReservationsForItem } from '@/modules/reservations/services/reservationRepository';
import { RESERVATION_STATUS_LABELS } from '@/modules/reservations/types';
import { listMovementsForItem } from '@/services/inventoryMovements/movementRepository';
import { MOVEMENT_TYPE_LABELS } from '@/services/inventoryMovements/types';
import type { TimelineEntry } from '@/services/inventoryMovements/types';
import { getFirestoreDb } from '@/services/firebase';
import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { appConfig } from '@/config/app';

const LOGISTICS_COLLECTION = 'logisticsOrders';

export async function buildItemOperationalTimeline(
  itemId: string,
): Promise<TimelineEntry[]> {
  const [movements, reservations, maintenance] = await Promise.all([
    listMovementsForItem(itemId),
    listReservationsForItem(itemId, '1970-01-01', '2099-12-31'),
    listMaintenanceTasksForItem(itemId),
  ]);

  const logisticsSnap = await getDocs(
    query(
      collection(getFirestoreDb(), LOGISTICS_COLLECTION),
      where('companyId', '==', appConfig.companyId),
    ),
  );
  type LogisticsRow = {
    id: string;
    inventoryItemIds?: string[];
    scheduledDate?: string;
    type?: string;
    status?: string;
  };
  const logistics = logisticsSnap.docs
    .map((d) => ({ id: d.id, ...d.data() } as LogisticsRow))
    .filter(
      (o) =>
        Array.isArray(o.inventoryItemIds) &&
        o.inventoryItemIds.includes(itemId),
    );

  const entries: TimelineEntry[] = [];

  for (const m of movements) {
    entries.push({
      id: `mov-${m.id}`,
      source: 'movement',
      occurredAt: m.createdAt.toISOString(),
      title: MOVEMENT_TYPE_LABELS[m.type],
      subtitle: [m.fromLocationId, m.toLocationId]
        .filter(Boolean)
        .join(' → ') || m.notes || '—',
      typeLabel: m.type,
      entityId: m.id,
    });
  }

  for (const r of reservations) {
    entries.push({
      id: `res-${r.id}`,
      source: 'reservation',
      occurredAt: r.createdAt.toISOString(),
      title: r.title,
      subtitle: `${r.startDate} → ${r.endDate}`,
      typeLabel: RESERVATION_STATUS_LABELS[r.status],
      entityId: r.id,
    });
  }

  for (const t of maintenance) {
    entries.push({
      id: `mnt-${t.id}`,
      source: 'maintenance',
      occurredAt: t.createdAt.toISOString(),
      title: t.title,
      subtitle: `${MAINTENANCE_TYPE_LABELS[t.type]} · ${t.scheduledStart} → ${t.scheduledEnd}`,
      typeLabel: MAINTENANCE_STATUS_LABELS[t.status],
      entityId: t.id,
    });
  }

  for (const o of logistics) {
    const scheduled = o.scheduledDate ?? '';
    entries.push({
      id: `log-${o.id}`,
      source: 'logistics',
      occurredAt: scheduled
        ? new Date(`${scheduled}T12:00:00`).toISOString()
        : new Date().toISOString(),
      title: `Logística · ${o.type ?? ''}`,
      subtitle: scheduled,
      typeLabel: o.status ?? '',
      entityId: o.id,
    });
  }

  return entries.sort(
    (a, b) =>
      new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
  );
}
